/**
 * **牌面几何的纯函数层。**
 *
 * ⚠️ 这个文件**不 import Pixi**：牌面的几何是最容易错、又最难靠肉眼定位的
 * 东西（多边形裁歪、路径抖断、网点漏出边界、虚线长度不对），
 * 放在纯函数里才能在 node 里断言。
 *
 * ## 坐标系
 *
 * 固定是**设计空间 `100 × 140`** —— 和 `metrics.CARD_WIDTH/HEIGHT` 相等。
 * 画法只在这个空间里描述"画在哪"，由 painter 负责缩放到实际 FBO 尺寸。
 * 这样牌面的数字永远是可以直接读、直接调、直接对着打样核对的。
 */

export interface Pt {
  readonly x: number;
  readonly y: number;
}

export const pt = (x: number, y: number): Pt => ({ x, y });

export const TAU = Math.PI * 2;

/** 设计空间的宽高。**必须**等于 `metrics.CARD_WIDTH` / `CARD_HEIGHT` */
export const DESIGN_WIDTH = 100;
export const DESIGN_HEIGHT = 140;

// ============================================================
// 种子噪声
// ============================================================

/**
 * 字符串 → 32 位种子。
 *
 * ⚠️ 牌面是**静态资产**：同一张牌每次都必须画得一模一样。
 * 所以一切"随机"都从这里派生，绝不用 `Math.random`。
 */
export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32：确定、够用、不啰嗦 */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 手抖：一条随时间平滑变化的偏移量。
 *
 * 叠到任何理想曲线上去，理想曲线就变成"手画的"。
 * ⚠️ 三个谐波是刻意不成整数比的 —— 成了整数比就会看出来是周期函数。
 */
export function tremor(seed: number, amp: number, freq: number): (t: number) => number {
  const r = rng(seed);
  const o0 = r() * TAU;
  const o1 = r() * TAU;
  const o2 = r() * TAU;
  return (t) =>
    amp *
    (Math.sin(t * freq * TAU + o0) * 0.6 +
      Math.sin(t * freq * 2.9 * TAU + o1) * 0.28 +
      Math.sin(t * freq * 6.3 * TAU + o2) * 0.12);
}

/** 把一条理想路径抖成手绘路径 */
export function hand(
  pts: readonly Pt[],
  seed: number,
  amp = 1.2,
  freq = 1.5,
): Pt[] {
  const nx = tremor(seed, amp, freq);
  const ny = tremor(seed ^ 0x9e3779b9, amp * 1.15, freq * 0.82);
  const last = Math.max(1, pts.length - 1);
  return pts.map((p, i) => pt(p.x + nx(i / last), p.y + ny(i / last)));
}

// ============================================================
// 基本形状
// ============================================================

/** 椭圆/圆的控制点。`a0`/`a1` 可以只取一段（做开口的环） */
export function ring(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  n = 48,
  a0 = 0,
  a1 = TAU,
): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push(pt(cx + rx * Math.cos(a), cy + ry * Math.sin(a)));
  }
  return out;
}

/** 矩形，顺时针。给"牌堆横条"这类东西用 */
export function rect(x: number, y: number, w: number, h: number): Pt[] {
  return [pt(x, y), pt(x + w, y), pt(x + w, y + h), pt(x, y + h)];
}

/**
 * 闭合 Catmull-Rom：几个控制点 → 一条圆滑轮廓。
 *
 * ⚠️ 这是"涂鸦"和"多边形"的分界线 —— 手画的头是圆的，不是七边形。
 */
export function smoothClosed(pts: readonly Pt[], n = 9): Pt[] {
  const m = pts.length;
  if (m < 3) return [...pts];
  const out: Pt[] = [];
  for (let i = 0; i < m; i++) {
    const p0 = pts[(i - 1 + m) % m]!;
    const p1 = pts[i]!;
    const p2 = pts[(i + 1) % m]!;
    const p3 = pts[(i + 2) % m]!;
    for (let k = 0; k < n; k++) {
      const t = k / n;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push(
        pt(
          0.5 *
            (2 * p1.x +
              (-p0.x + p2.x) * t +
              (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
              (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
          0.5 *
            (2 * p1.y +
              (-p0.y + p2.y) * t +
              (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
              (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
        ),
      );
    }
  }
  return out;
}

/** 绕一点旋转（角度制）。手柄、宝石这类"整体歪一点"的构件用它 */
export function rotateAbout(pts: readonly Pt[], deg: number, cx: number, cy: number): Pt[] {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return pts.map((p) => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    return pt(cx + dx * c - dy * s, cy + dx * s + dy * c);
  });
}

// ============================================================
// 多边形运算
// ============================================================

export interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export function bounds(pts: readonly Pt[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** 射线法。奇数个交点 = 在里面 */
export function pointInPoly(p: Pt, poly: readonly Pt[]): boolean {
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Sutherland–Hodgman：把多边形裁到 `nx*x + ny*y <= d` 这半边。
 *
 * ⚠️ 45° 条纹和网点全靠它 —— 用裁剪算出来的条纹是**精确几何**，
 * 不依赖贴图采样，所以和分辨率无关，也能在 node 里断言。
 * `(nx, ny)` 不必归一化，但归一化了 d 才等于"到原点的距离"。
 */
export function clipHalfPlane(
  poly: readonly Pt[],
  nx: number,
  ny: number,
  d: number,
): Pt[] {
  const n = poly.length;
  const out: Pt[] = [];
  if (n === 0) return out;
  const f = (p: Pt): number => nx * p.x + ny * p.y - d;
  for (let i = 0; i < n; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % n]!;
    const fa = f(a);
    const fb = f(b);
    if (fa <= 0) out.push(a);
    if ((fa < 0) !== (fb < 0)) {
      const t = fa / (fa - fb);
      out.push(pt(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t));
    }
  }
  return out;
}

/** 裁到一个"带"里：`lo <= nx*x + ny*y <= hi` */
export function clipBand(
  poly: readonly Pt[],
  nx: number,
  ny: number,
  lo: number,
  hi: number,
): Pt[] {
  return clipHalfPlane(clipHalfPlane(poly, -nx, -ny, -lo), nx, ny, hi);
}

/** 多边形面积。裁完剩一条线（面积 0）的碎片要丢掉，否则会画出蜘蛛丝 */
export function polyArea(poly: readonly Pt[]): number {
  let a = 0;
  const n = poly.length;
  for (let i = 0; i < n; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % n]!;
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
}

// ============================================================
// 折线处理
// ============================================================

/**
 * 按虚线模式把一条折线切成若干段。
 *
 * ⚠️ Pixi 的 Graphics 没有 dash —— 所以虚线必须在**几何层**切好。
 * 这反而更好：切出来的是实心段，不受线宽和端点样式影响。
 */
export function dashPolyline(pts: readonly Pt[], pattern: readonly number[]): Pt[][] {
  if (pts.length < 2) return [];
  // ⚠️ 把 0 和负数滤掉：它们会让 `left` 变成 0 或负数，内层循环永远不推进
  const pat = pattern.filter((v) => v > 0);
  if (pat.length === 0) return [[...pts]];

  const out: Pt[][] = [];
  let pi = 0;
  let left = pat[0]!;
  let on = true;
  let seg: Pt[] = [pts[0]!];

  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len === 0) {
      if (on) seg.push(b);
      continue;
    }
    // ⚠️ `done` 是**沿这一段的绝对进度**。第一版把它写成"剩余长度"，
    //    于是每切一刀分母都在缩水 —— 十段之后虚线就偏到不知哪里去了。
    //    这个 bug 肉眼看不出来（虚线还是虚线），只有量总长才发现。
    let done = 0;
    while (done + left < len) {
      done += left;
      const t = done / len;
      const m = pt(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
      if (on) {
        seg.push(m);
        if (seg.length >= 2) out.push(seg);
      }
      seg = [m];
      pi = (pi + 1) % pat.length;
      left = pat[pi]!;
      on = !on;
    }
    left -= len - done;
    if (on) seg.push(b);
  }
  if (on && seg.length >= 2) out.push(seg);
  return out;
}

/** `Pt[]` → Pixi 的扁平数组 `[x0,y0,x1,y1,...]`。 painter 用它 */
export function flatten(pts: readonly Pt[]): number[] {
  const out: number[] = [];
  for (const p of pts) out.push(p.x, p.y);
  return out;
}
